/**
 * ReportsAppointments — the Appointments reports family (APT-10): the merge of
 * the three-in-one's two tenant-split report designs — HE's health metrics
 * (kept / no-show / rescheduled by category) + the bank's booking funnel —
 * into ONE tenant-neutral tab of GM Reports. Wired to getAppointmentsReport
 * (declared, REQ-089; seed-served in preview — DataSourceStrip). Reuses the
 * house report primitives (ScoreTile / RangeSelect / LocationSelect / tabs).
 */
import { useEffect, useState } from "react";
import { Btn, Card, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { useNav } from "../../shell/router";
import { useT } from "../../lib/i18n";
import { reportTabs, RangeSelect, LocationSelect, ScoreTile, fmtNum, fmtPct, delta } from "./ReportsOverview";
import { getAppointmentsReport, type AppointmentsReport } from "../appointments/appointments-api";

const ORIGIN_LABEL: Record<string, string> = { journey: "Online (journey)", staff: "Staff-booked", concierge: "Concierge", customers: "Customer 360", guests: "Guest follow-up", book: "Day book", import: "Imported" };

function Bar({ label, value, max, accent }: { label: string; value: number; max: number; accent: string }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "130px 1fr 48px", gap: 10, alignItems: "center" }}>
      <span style={{ fontSize: 12.5, color: T.n1, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      <div style={{ height: 10, background: T.n6, borderRadius: 999, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${max > 0 ? Math.max(2, (value / max) * 100) : 0}%`, background: accent, borderRadius: 999, transition: "width 400ms" }} />
      </div>
      <span style={{ fontSize: 12.5, color: T.n1, fontWeight: 700, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtNum(value)}</span>
    </div>
  );
}

export default function ReportsAppointments() {
  const t = useT();
  const nav = useNav();
  const [range, setRange] = useState("30d");
  const [loc, setLoc] = useState("all");
  const [data, setData] = useState<AppointmentsReport | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => getAppointmentsReport({ range, locationId: loc === "all" ? null : loc })
    .then((r) => { setData(r); setErr(null); }).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  useEffect(() => { load(); }, [range, loc]);

  const k = data?.kpis;
  const funnelMax = Math.max(1, ...(data?.funnel ?? []).map((f) => f.count));
  const svcMax = Math.max(1, ...(data?.byService ?? []).map((s) => s.booked));

  return (
    <Screen rail="reports" tabs={reportTabs(nav, "appointments")} activeTab="appointments">
      <PageHeader
        title={t("reports.appointments.appointments", "Appointments")}
        subtitle={t("reports.appointments.subtitle", "Booking health across every channel — kept, no-show and rescheduled, by service and staff.")}
        actions={<div style={{ display: "flex", gap: 8 }}>
          <LocationSelect value={loc} onChange={setLoc} locs={data?.locations ?? []} />
          <RangeSelect value={range} onChange={setRange} />
        </div>}
      />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 32px", display: "flex", flexDirection: "column", gap: 16 }}>
        {err ? (
          <div style={{ padding: "14px 16px", borderRadius: 10, background: "#FDECEC", border: "1px solid #F5B5B5", color: "#8A1F1F", fontSize: 13 }}>
            {t("reports.appointments.loadFailed", "Couldn't load the appointments report")} — {err} <Btn size="sm" variant="secondary" onClick={load} style={{ marginLeft: 8 }}>{t("appointments.book.retry", "Retry")}</Btn>
          </div>
        ) : !data || !k ? (
          <div style={{ fontSize: 13, color: T.soft, padding: "24px 0" }}>{t("common.status.loading", "Loading…")}</div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
              <ScoreTile label={t("reports.appointments.booked", "Booked")} value={fmtNum(k.booked)} accent="#5142C7" sub={t("reports.appointments.allChannels", "all channels")} />
              <ScoreTile label={t("reports.appointments.keptRate", "Kept rate")} value={fmtPct(k.keptRate)} accent="#1F8A5B" sub={`${fmtNum(k.kept)} ${t("reports.appointments.kept", "kept")}`} />
              <ScoreTile label={t("reports.appointments.noShowRate", "No-show rate")} value={fmtPct(k.noShowRate)} accent="#C77F14" sub={`${fmtNum(k.noShow)} ${t("reports.appointments.noShows", "no-shows")}`} />
              <ScoreTile label={t("reports.appointments.rescheduled", "Rescheduled")} value={fmtNum(k.rescheduled)} accent="#2E6E8E" sub={`${fmtNum(k.cancelled)} ${t("reports.appointments.cancelled", "cancelled")}`} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
              <Card style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700 }}>{t("reports.appointments.lifecycleFunnel", "Lifecycle funnel")}</div>
                {data.funnel.map((f) => <Bar key={f.stage} label={f.stage} value={f.count} max={funnelMax} accent="#5142C7" />)}
              </Card>
              <Card style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700 }}>{t("reports.appointments.howItWasBooked", "How it was booked")}</div>
                {data.byOrigin.map((o) => <Bar key={o.origin} label={ORIGIN_LABEL[o.origin] ?? o.origin} value={o.count} max={Math.max(1, ...data.byOrigin.map((x) => x.count))} accent="#2E6E8E" />)}
              </Card>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
              <Card style={{ padding: 0 }}>
                <div style={{ padding: "14px 18px 10px", fontSize: 13.5, fontWeight: 700 }}>{t("reports.appointments.byService", "By service")}</div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                  <thead><tr style={{ color: T.soft, textAlign: "left" }}>
                    <th style={{ padding: "6px 18px", fontWeight: 650 }}>{t("reports.appointments.service", "Service")}</th>
                    <th style={{ padding: "6px 8px", fontWeight: 650, textAlign: "right" }}>{t("reports.appointments.booked", "Booked")}</th>
                    <th style={{ padding: "6px 8px", fontWeight: 650, textAlign: "right" }}>{t("reports.appointments.kept", "kept")}</th>
                    <th style={{ padding: "6px 18px", fontWeight: 650, textAlign: "right" }}>{t("reports.appointments.noShows", "no-shows")}</th>
                  </tr></thead>
                  <tbody>
                    {data.byService.map((s) => (
                      <tr key={s.id} style={{ borderTop: `1px solid ${T.n6}` }}>
                        <td style={{ padding: "9px 18px", fontWeight: 600, color: T.n1 }}>
                          {s.name}
                          <div style={{ height: 4, background: T.n6, borderRadius: 999, marginTop: 5, overflow: "hidden", maxWidth: 180 }}><div style={{ height: "100%", width: `${(s.booked / svcMax) * 100}%`, background: "#5142C7" }} /></div>
                        </td>
                        <td style={{ padding: "9px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{s.booked}</td>
                        <td style={{ padding: "9px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#1F8A5B" }}>{s.kept}</td>
                        <td style={{ padding: "9px 18px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#C77F14" }}>{s.noShow}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
              <Card style={{ padding: 0 }}>
                <div style={{ padding: "14px 18px 10px", fontSize: 13.5, fontWeight: 700 }}>{t("reports.appointments.byStaff", "By staff")}</div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                  <thead><tr style={{ color: T.soft, textAlign: "left" }}>
                    <th style={{ padding: "6px 18px", fontWeight: 650 }}>{t("reports.appointments.staffMember", "Staff member")}</th>
                    <th style={{ padding: "6px 8px", fontWeight: 650, textAlign: "right" }}>{t("reports.appointments.booked", "Booked")}</th>
                    <th style={{ padding: "6px 18px", fontWeight: 650, textAlign: "right" }}>{t("reports.appointments.kept", "kept")}</th>
                  </tr></thead>
                  <tbody>
                    {data.byStaff.map((s) => (
                      <tr key={s.id} style={{ borderTop: `1px solid ${T.n6}` }}>
                        <td style={{ padding: "9px 18px", fontWeight: 600, color: T.n1 }}>{s.name}</td>
                        <td style={{ padding: "9px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{s.booked}</td>
                        <td style={{ padding: "9px 18px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#1F8A5B" }}>{s.kept}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
          </>
        )}
        <DataSourceStrip screen="reports-appointments" ops={["getAppointmentsReport"]} reqId="REQ-089" />
      </div>
    </Screen>
  );
}
